import React, { useState, useEffect, useMemo } from 'react';
import {
    Box, Typography, Button, Paper, Grid, TextField, Select, MenuItem, FormControl,
    InputLabel, Checkbox, FormControlLabel, CircularProgress, Link, Alert
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, MessageSquare, Briefcase, TrendingUp, Star, Download } from 'lucide-react';
import { Person } from '../../types/person';
import { PersonService } from '../../services/personService';
import FeedbackForm from './FeedbackForm'; // Importar o novo componente
import DirectorateChangeForm from './DirectorateChangeForm'; // Importar novo componente
import jobRoleService, { JobRole } from '../../services/jobRoleService'; // Importar
import RoleLevelChangeForm from './RoleLevelChangeForm'; // Importar
import OtherEventForm from './OtherEventForm'; // Importar
import personFeedService from '../../services/personFeedService'; // Importar o novo serviço

const formatDateForInput = (date: Date): string => {
    return date.toISOString().split('T')[0];
};

const PersonFeedFormPage: React.FC = () => {
    const navigate = useNavigate();
    const params = useParams();
    const personService = useMemo(() => new PersonService(), []);
    const eventId = params.id;
    const isViewMode = !!eventId;

    // Estados de controle
    const [isLoading, setIsLoading] = useState(isViewMode);
    const [loadError, setLoadError] = useState<string | null>(null);

    // Estados do formulário
    const [people, setPeople] = useState<Person[]>([]);
    const [peopleLoading, setPeopleLoading] = useState(true);
    const [selectedPersonId, setSelectedPersonId] = useState('');
    const [eventDate, setEventDate] = useState<string>(formatDateForInput(new Date()));
    const [selectedEventTypes, setSelectedEventTypes] = useState<string[]>([]);

    // --- Estados para o formulário de Feedback ---
    const [feedbackParticipants, setFeedbackParticipants] = useState<Person[]>([]);
    const [feedbackSummary, setFeedbackSummary] = useState('');

    // --- Estados para o formulário de Mudança de Diretoria ---
    const [newDirectorate, setNewDirectorate] = useState('');
    const [directorateJustification, setDirectorateJustification] = useState('');

    // --- Estados para o formulário de Mudança de Cargo/Nível ---
    const [jobRoles, setJobRoles] = useState<JobRole[]>([]);
    const [jobRolesLoading, setJobRolesLoading] = useState(true);
    const [selectedRoleId, setSelectedRoleId] = useState('');
    const [selectedLevelId, setSelectedLevelId] = useState('');
    const [roleLevelJustification, setRoleLevelJustification] = useState('');

    // --- Estados para o formulário de Outro Evento ---
    const [otherEventDescription, setOtherEventDescription] = useState('');

    // --- Estado para anexos (do FeedbackForm) ---
    const [attachments, setAttachments] = useState<File[]>([]);
    const [existingAttachments, setExistingAttachments] = useState<any[]>([]); // Para modo de visualização


    // --- Estados de controle (loading/error) ---
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);


    // Filtra a lista de pessoas para encontrar apenas os diretores
    const directors = useMemo(() => {
         if (peopleLoading) return [];
         return people.filter(p => p.current_role === 'Diretor');
    }, [people, peopleLoading]);


    useEffect(() => {
        const fetchInitialData = async () => {
            setPeopleLoading(true);
            setJobRolesLoading(true);
            try {
                const peoplePromise = personService.getAll({ active: true, pageSize: 9999 });
                const jobRolesPromise = jobRoleService.getAllJobRoles();
                
                const [peopleResponse, jobRolesResponse] = await Promise.all([peoplePromise, jobRolesPromise]);

                setPeople(peopleResponse.data);
                setJobRoles(jobRolesResponse);

            } catch (error) {
                console.error("Erro ao buscar dados iniciais:", error);
                setLoadError("Falha ao carregar dados de suporte do formulário.");
            } finally {
                setPeopleLoading(false);
                setJobRolesLoading(false);
            }
        };

        const fetchEventData = async () => {
            if (!eventId) return;
            setIsLoading(true);
            setLoadError(null);
            try {
                const eventData = await personFeedService.getEventById(eventId);
                
                // Preencher todos os estados com os dados do evento
                setSelectedPersonId(eventData.person_id.toString());
                setEventDate(formatDateForInput(new Date(eventData.event_date)));
                
                // CORREÇÃO: Mapeia do formato DB (FOO_BAR) para o formato do frontend (fooBar)
                const toCamelCase = (s: string) => s.toLowerCase().replace(/(_\w)/g, m => m.substring(1).toUpperCase());
                const eventTypes = eventData.event_types.map(toCamelCase);
                setSelectedEventTypes(eventTypes);
                
                if (eventData.details.feedback) {
                    setFeedbackSummary(eventData.details.feedback.summary);
                    setFeedbackParticipants(eventData.details.feedback.participantsDetails || []);
                }
                if (eventData.details.directorateChange) {
                    setNewDirectorate(eventData.details.directorateChange.newDirectorateId.toString());
                    setDirectorateJustification(eventData.details.directorateChange.justification || '');
                }
                if (eventData.details.roleLevelChange) {
                    setSelectedRoleId(eventData.details.roleLevelChange.newRoleId.toString());
                    setSelectedLevelId(eventData.details.roleLevelChange.newLevelId?.toString() || '');
                    setRoleLevelJustification(eventData.details.roleLevelChange.justification || '');
                }
                if (eventData.details.otherEvent) {
                    setOtherEventDescription(eventData.details.otherEvent.description);
                }
                if (eventData.attachments) {
                    setExistingAttachments(eventData.attachments);
                }

            } catch (error) {
                setLoadError("Não foi possível carregar os detalhes deste evento.");
            } finally {
                setIsLoading(false);
            }
        };

        fetchInitialData();
        if (isViewMode) {
            fetchEventData();
        }
    }, [eventId, isViewMode, personService]);

    const handleEventTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, checked } = event.target;
        setSelectedEventTypes(prev =>
            checked ? [...prev, name] : prev.filter(type => type !== name)
        );
    };

    const handleCancel = () => {
        navigate('/person-feed');
    };

    const handleSave = async () => {
        setIsSaving(true);
        setSaveError(null);

        const formData = new FormData();

        // 1. Campos básicos
        formData.append('personId', selectedPersonId);
        formData.append('eventDate', eventDate);
        
        // O backend espera um array, então adicionamos cada tipo
        selectedEventTypes.forEach(type => {
            formData.append('eventTypes[]', type.toUpperCase());
        });

        // 2. Detalhes condicionais
        if (selectedEventTypes.includes('feedback')) {
            formData.append('feedbackSummary', feedbackSummary);
            formData.append('feedbackParticipants', feedbackParticipants.map(p => p.id).join(','));
            // Anexar arquivos
            attachments.forEach(file => {
                formData.append('attachments', file);
            });
        }
        if (selectedEventTypes.includes('directorateChange')) {
            formData.append('newDirectorate', newDirectorate);
            formData.append('directorateJustification', directorateJustification);
        }
        if (selectedEventTypes.includes('roleLevelChange')) {
            formData.append('newRoleId', selectedRoleId);
            formData.append('newLevelId', selectedLevelId);
            formData.append('roleLevelJustification', roleLevelJustification);
        }
        if (selectedEventTypes.includes('otherEvent')) {
            formData.append('otherEventDescription', otherEventDescription);
        }
        
        try {
            await personFeedService.createEvent(formData);
            navigate('/person-feed'); // Sucesso: volta para a lista
        } catch (error) {
            setSaveError(error as string);
        } finally {
            setIsSaving(false);
        }
    };
    
    const eventTypes = [
        { name: 'feedback', label: 'Feedback', icon: <MessageSquare size={18} /> },
        { name: 'directorateChange', label: 'Mudança de Diretoria', icon: <Briefcase size={18} /> },
        { name: 'roleLevelChange', label: 'Mudança de Cargo / Nível', icon: <TrendingUp size={18} /> },
        { name: 'otherEvent', label: 'Outro Evento', icon: <Star size={18} /> },
    ];

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h5" component="h1">
                    {isViewMode ? 'Detalhes do Evento' : 'Feed de Pessoas - Novo Evento'}
                </Typography>
                <Button 
                    variant="outlined" 
                    onClick={handleCancel} 
                    startIcon={<ArrowLeft size={16} />} 
                    size="small" 
                    sx={{ px: 2, py: 0.5, height: 36, fontSize: '0.75rem', borderRadius: 1 }}
                >
                    VOLTAR
                </Button>
            </Box>

            <Paper sx={{ p: 2, mt: 2 }}>
                {isLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
                ) : loadError ? (
                    <Alert severity="error">{loadError}</Alert>
                ) : (
                <>
                <Typography variant="subtitle1" sx={{ fontSize: '0.9rem', fontWeight: 'bold', mb: 2 }}>Informações Básicas</Typography>
                <Grid container spacing={1.5}>
                    <Grid item xs={12} md={6}>
                        <FormControl fullWidth size="small" margin="dense" disabled={peopleLoading || isViewMode}>
                            <InputLabel id="person-select-label" sx={{ fontSize: '0.85rem' }}>Pessoa *</InputLabel>
                            <Select
                                labelId="person-select-label"
                                value={selectedPersonId}
                                label="Pessoa *"
                                onChange={(e) => setSelectedPersonId(e.target.value)}
                                sx={{ fontSize: '0.85rem', height: 40 }}
                                renderValue={(selected) => {
                                    if (peopleLoading) {
                                        return <em>Carregando...</em>;
                                    }
                                    const selectedPerson = people.find(p => p.id === Number(selected));
                                    return selectedPerson ? selectedPerson.name : '';
                                }}
                            >
                                {peopleLoading ? (
                                    <MenuItem value="" disabled sx={{ justifyContent: 'center' }}>
                                        <CircularProgress size={20} />
                                    </MenuItem>
                                ) : (
                                    people.map(person => (
                                        <MenuItem key={person.id} value={person.id} sx={{ fontSize: '0.85rem' }}>
                                            {person.name}
                                        </MenuItem>
                                    ))
                                )}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Data do Evento *"
                            type="date"
                            value={eventDate}
                            onChange={(e) => setEventDate(e.target.value)}
                            size="small"
                            margin="dense"
                            InputLabelProps={{ shrink: true, style: { fontSize: '0.85rem' } }}
                            InputProps={{ style: { fontSize: '0.85rem' }, sx: { height: 40 } }}
                            disabled={isViewMode}
                        />
                    </Grid>
                </Grid>

                <Typography variant="subtitle1" sx={{ fontSize: '0.9rem', fontWeight: 'bold', mt: 3, mb: 2 }}>
                    {isViewMode ? 'Eventos Registrados' : 'O que você deseja registrar?'}
                </Typography>

                <Grid container spacing={1.5}>
                    {eventTypes.map((type) => (
                        <Grid item xs={12} sm={6} md={3} key={type.name}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={selectedEventTypes.includes(type.name)}
                                        onChange={handleEventTypeChange}
                                        name={type.name}
                                        size="small"
                                        sx={{ transform: 'scale(0.85)', p: 0.5 }}
                                        disabled={isViewMode}
                                    />
                                }
                                label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, fontSize: '0.85rem' }}>
                                        {type.icon}
                                        {type.label}
                                    </Box>
                                }
                                sx={{
                                    width: '100%',
                                    height: 40,
                                    border: 1,
                                    borderColor: 'divider',
                                    borderRadius: 1,
                                    px: 1.5,
                                    m: 0,
                                    bgcolor: selectedEventTypes.includes(type.name) ? 'action.hover' : 'transparent',
                                    transition: 'background-color 0.2s',
                                    '&:hover': {
                                        borderColor: 'primary.main'
                                    }
                                }}
                            />
                        </Grid>
                    ))}
                </Grid>

                {/* --- Seção Condicional de Feedback --- */}
                {selectedEventTypes.includes('feedback') && (
                    <FeedbackForm
                        people={people}
                        participants={feedbackParticipants}
                        setParticipants={setFeedbackParticipants}
                        summary={feedbackSummary}
                        setSummary={setFeedbackSummary}
                        attachments={attachments}
                        setAttachments={setAttachments}
                        existingAttachments={existingAttachments}
                        isViewMode={isViewMode}
                    />
                )}

                {/* --- Seção Condicional de Mudança de Diretoria --- */}
                {selectedEventTypes.includes('directorateChange') && (
                    <DirectorateChangeForm
                        people={directors}
                        newDirectorate={newDirectorate}
                        setNewDirectorate={setNewDirectorate}
                        justification={directorateJustification}
                        setJustification={setDirectorateJustification}
                        isViewMode={isViewMode}
                    />
                )}

                {/* --- Seção Condicional de Mudança de Cargo/Nível --- */}
                {selectedEventTypes.includes('roleLevelChange') && (
                    <RoleLevelChangeForm
                        jobRoles={jobRoles}
                        selectedRoleId={selectedRoleId}
                        setSelectedRoleId={setSelectedRoleId}
                        selectedLevelId={selectedLevelId}
                        setSelectedLevelId={setSelectedLevelId}
                        justification={roleLevelJustification}
                        setJustification={setRoleLevelJustification}
                        isViewMode={isViewMode}
                    />
                )}

                {/* --- Seção Condicional de Outro Evento --- */}
                {selectedEventTypes.includes('otherEvent') && (
                    <OtherEventForm
                        description={otherEventDescription}
                        setDescription={setOtherEventDescription}
                        isViewMode={isViewMode}
                    />
                )}
                {saveError && <Alert severity="error" sx={{ mt: 2 }}>{saveError}</Alert>}
                
                {!isViewMode && (
                <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
                    <Button
                        variant="outlined"
                        onClick={handleCancel}
                        size="small" 
                        sx={{ px: 2, py: 0.5, height: 36, fontSize: '0.75rem', borderRadius: 1 }}
                    >
                        CANCELAR
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<Save size={16} />}
                        onClick={handleSave}
                        size="small" 
                        sx={{ px: 2, py: 0.5, height: 36, fontWeight: 'bold', fontSize: '0.75rem', borderRadius: 1 }}
                        disabled={isSaving}
                    >
                        {isSaving ? <CircularProgress size={20} color="inherit" /> : 'SALVAR REGISTRO'}
                    </Button>
                </Box>
                )}
                </>
            )}
            </Paper>
        </Box>
    );
};

export default PersonFeedFormPage;
